import type {
  CreateCaseAuditFeedbackRequest,
  UpdateCaseAuditRequest,
} from '@compass/client/types/audit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { auditService } from '@/services/api';
import { useStore } from '@/store/store';

interface UseCaseAuditOptions {
  caseRecordId: string;
  /** Gate listCaseAudits. Caller should pass true once the Review tab is active. */
  auditsEnabled?: boolean;
  /** Gate listCaseAuditFeedback. Caller should pass true only when the Feedback view is open. */
  feedbackEnabled?: boolean;
}

const PAGE_SIZE = 50;

function useAuditsForCase(args: {
  caseRecordId: string;
  organizationId: string | null;
  programId: string | null;
  enabled: boolean;
}) {
  const { caseRecordId, organizationId, programId, enabled } = args;
  return useQuery({
    queryKey: ['caseAudit', 'audits', caseRecordId, organizationId, programId],
    queryFn: () =>
      auditService.listCaseAudits({
        organization_id: organizationId!,
        program_id: programId!,
        case_id: caseRecordId,
        limit: PAGE_SIZE,
      }),
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // Poll so a human-audit entity created/finalized out-of-band (BI-1246: the
    // Start action creates an in-progress row; a terminal action marks it
    // completed) surfaces in the tab strip without a manual refresh.
    refetchInterval: 5 * 1000,
  });
}

function useFeedbackForCase(args: {
  caseRecordId: string;
  organizationId: string | null;
  programId: string | null;
  enabled: boolean;
}) {
  const { caseRecordId, organizationId, programId, enabled } = args;
  return useQuery({
    queryKey: ['caseAudit', 'feedback', caseRecordId, organizationId, programId],
    queryFn: () =>
      auditService.listCaseAuditFeedback({
        organization_id: organizationId!,
        program_id: programId!,
        case_id: caseRecordId,
        limit: PAGE_SIZE,
      }),
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch the LLM-audit runs and human ground-truth feedback for a case record.
 * Both queries are gated by `enabled` so they only fire when the case audit
 * tab is active.
 */
function computeEnabled(args: {
  enabled: boolean;
  caseRecordId: string;
  organizationId: string | null;
  programId: string | null;
}): boolean {
  if (!args.enabled) return false;
  if (!args.caseRecordId) return false;
  if (!args.organizationId) return false;
  if (!args.programId) return false;
  return true;
}

export function useCaseAudit({
  caseRecordId,
  auditsEnabled = true,
  feedbackEnabled = true,
}: UseCaseAuditOptions) {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);

  const auditsQuery = useAuditsForCase({
    caseRecordId,
    organizationId,
    programId,
    enabled: computeEnabled({
      enabled: auditsEnabled,
      caseRecordId,
      organizationId,
      programId,
    }),
  });
  const feedbackQuery = useFeedbackForCase({
    caseRecordId,
    organizationId,
    programId,
    enabled: computeEnabled({
      enabled: feedbackEnabled,
      caseRecordId,
      organizationId,
      programId,
    }),
  });

  const auditsData = auditsQuery.data;
  const feedbackData = feedbackQuery.data;

  // Memoize the empty-array fallback so callers receive a stable reference
  // across renders when no data has loaded. Without this, `?? []` produces a
  // fresh array each render, cascading into every memo/effect that depends on
  // `audits`/`feedback` (e.g. unifiedTabItems → displayData.outputData →
  // committedOutput effect) and causing an infinite render loop.
  const audits = useMemo(() => auditsData?.audits ?? [], [auditsData?.audits]);
  const feedback = useMemo(() => feedbackData?.feedback ?? [], [feedbackData?.feedback]);

  const refetch = useCallback(() => {
    auditsQuery.refetch();
    feedbackQuery.refetch();
  }, [auditsQuery, feedbackQuery]);

  return {
    audits,
    auditsNextCursor: auditsData ? auditsData.next_cursor : undefined,
    feedback,
    feedbackNextCursor: feedbackData ? feedbackData.next_cursor : undefined,
    isLoading: auditsQuery.isLoading || feedbackQuery.isLoading,
    // Per-query errors so callers can surface them independently (the audits
    // panel can render while feedback is still failing/loading, and vice versa).
    auditsError: auditsQuery.error,
    feedbackError: feedbackQuery.error,
    // Convenience: first error (either query). Prefer the per-query fields above
    // when you need to know which one failed.
    error: auditsQuery.error || feedbackQuery.error,
    refetch,
  };
}

/**
 * Mutation hook for `UpdateCaseAudit` on an audit of `caseRecordId`. The
 * case-scoped audit routes let a Case Manager reach the audits of cases
 * assigned to them. Caller supplies the rest of the UpdateCaseAuditRequest body;
 * on success we invalidate the cached audits list so the case-audit tab
 * re-renders with the new values.
 */
export function useUpdateAudit(caseRecordId: string) {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      req: Omit<UpdateCaseAuditRequest, 'organization_id' | 'program_id' | 'case_id'>,
    ) => {
      if (!organizationId || !programId || !caseRecordId) {
        throw new Error('Organization, program and case must be selected');
      }
      return auditService.updateCaseAudit({
        organization_id: organizationId,
        program_id: programId,
        case_id: caseRecordId,
        ...req,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseAudit', 'audits'] });
    },
  });
}

/**
 * Mutation hook for `CreateCaseAuditFeedback`. Records a human ground-truth
 * annotation (👍/👎 + optional note) against an audit run of `caseRecordId`.
 * On success we invalidate the cached feedback list so the Feedback view
 * re-renders.
 */
export function useCreateAuditFeedback(caseRecordId: string) {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      req: Omit<CreateCaseAuditFeedbackRequest, 'organization_id' | 'program_id' | 'case_id'>,
    ) => {
      if (!organizationId || !programId || !caseRecordId) {
        throw new Error('Organization, program and case must be selected');
      }
      return auditService.createCaseAuditFeedback({
        organization_id: organizationId,
        program_id: programId,
        case_id: caseRecordId,
        ...req,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseAudit', 'feedback'] });
    },
  });
}
